import { z } from 'zod'
import { Model } from './architecture'
import { Count } from './Count'
import { PageNumber } from './PageNumber'
import type { GETLikesPropertiesResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/user-engagement/user/likes'

export const PaginationStatusValidation = z.object({
  total: z.instanceof(Count, { message: '[PaginationStatus] total of items is required' }),
  last: z.instanceof(PageNumber, { message: '[PaginationStatus] last page is required' }),
  page: z.instanceof(PageNumber, { message: '[PaginationStatus] current page is required' }),
})

export class PaginationStatus extends Model {
  static create({ total, last, page }: z.infer<typeof PaginationStatusValidation>) {
    PaginationStatusValidation.parse({ total, last, page })
    return new PaginationStatus(total, last, page)
  }

  static fromLikesAPI({ json }: { json: GETLikesPropertiesResponseType['pagination'] }) {
    return PaginationStatus.create({
      total: Count.create({ value: json.total }),
      last: PageNumber.create({ value: json.last }),
      page: PageNumber.create({ value: json.page }),
    })
  }

  static empty() {
    return new PaginationStatus(Count.empty(), PageNumber.empty(), PageNumber.empty())
  }

  static fromJSON(json: ReturnType<PaginationStatus['toJSON']>) {
    return PaginationStatus.create({
      total: Count.create({ value: json.total }),
      last: PageNumber.create({ value: json.last }),
      page: PageNumber.create({ value: json.page }),
    })
  }

  constructor(
    private readonly _total: Count,
    private readonly _last: PageNumber,
    private readonly _page: PageNumber
  ) {
    super()
  }

  get total() { return this._total.value } // prettier-ignore
  get last() { return this._last.value } // prettier-ignore
  get page() { return this._page.value } // prettier-ignore

  isEmpty() {
    return this.total === 0
  }

  isLastPage() {
    return this.page === this.last
  }

  nextPage() {
    if (this.isLastPage()) {
      return this
    }
    return PaginationStatus.create({
      total: Count.fromNumber(this.total),
      last: PageNumber.fromNumber(this.last),
      page: PageNumber.fromNumber(this.page + 1),
    })
  }

  toJSON() {
    return { total: this.total, last: this.last, page: this.page }
  }
}
